/**
 * ParentBalanceService - keeps the iframe balance in sync with its host.
 * Supports both the legacy PlayFortune `getBalance` contract and the newer
 * structured GET_BALANCE/BALANCE_UPDATE messages.
 */

import { AuthService } from "./authService";

export interface ParentMessage {
  type?: string;
  payload?: unknown;
  data?: unknown;
  value?: unknown;
  balance?: unknown;
  currency?: unknown;
  key?: unknown;
}

export interface GameMessage {
  type: "GET_BALANCE" | "BET_PLACED" | "CASHOUT" | "GAME_READY";
  payload?: unknown;
}

type BalanceCallback = (balance: number, currency: string) => void;

type ExtractedBalance = {
  balance?: number;
  currency?: string;
};

class ParentBalanceServiceClass {
  private callbacks = new Set<BalanceCallback>();
  private isInitialized = false;
  private lastBalanceRequestAt = 0;
  private readonly boundHandleMessage = (event: MessageEvent) =>
    this.handleMessage(event);

  initialize(): void {
    if (this.isInitialized || typeof window === "undefined") return;

    window.addEventListener("message", this.boundHandleMessage);
    this.isInitialized = true;
  }

  destroy(): void {
    if (typeof window !== "undefined") {
      window.removeEventListener("message", this.boundHandleMessage);
    }
    this.isInitialized = false;
    this.callbacks.clear();
  }

  private handleMessage(event: MessageEvent): void {
    // In an iframe, only the embedding parent may update the balance. During
    // direct local development, only same-window messages are considered.
    if (window.parent !== window) {
      if (event.source !== window.parent) return;
    } else if (event.source !== window) {
      return;
    }

    const update = this.extractBalanceUpdate(event.data);
    if (update.balance === undefined && !update.currency) return;

    const currency = update.currency || AuthService.getCurrency();
    if (update.currency) AuthService.setCurrency(update.currency);

    this.callbacks.forEach((callback) =>
      callback(update.balance ?? -1, currency),
    );
  }

  private extractBalanceUpdate(data: unknown): ExtractedBalance {
    if (!data) return {};

    if (typeof data === "string") {
      try {
        return this.extractBalanceUpdate(JSON.parse(data));
      } catch {
        return {};
      }
    }

    if (typeof data !== "object") return {};

    const queue: Record<string, unknown>[] = [
      data as Record<string, unknown>,
    ];
    const visited = new Set<object>();
    let currency: string | undefined;

    while (queue.length) {
      const item = queue.shift()!;
      if (visited.has(item)) continue;
      visited.add(item);

      const itemCurrency = item.currency ?? item.currencyCode;
      if (typeof itemCurrency === "string" && itemCurrency.trim()) {
        currency = itemCurrency.trim().toUpperCase();
      }

      const keyedBalance =
        typeof item.key === "string" &&
        item.key.toLowerCase().includes("balance")
          ? item.value
          : undefined;
      const rawBalance =
        item.balance ??
        item.availableBalance ??
        item.available_balance ??
        keyedBalance;
      const balance = this.toBalanceNumber(rawBalance);

      if (balance !== undefined) return { balance, currency };

      for (const key of [
        "payload",
        "data",
        "value",
        "userBalance",
        "wallet",
      ]) {
        const nested = item[key];
        if (nested && typeof nested === "object") {
          queue.push(nested as Record<string, unknown>);
        }
      }
    }

    return { currency };
  }

  private toBalanceNumber(value: unknown): number | undefined {
    let candidate = value;

    if (candidate && typeof candidate === "object") {
      const balanceObject = candidate as Record<string, unknown>;
      candidate =
        balanceObject.after ??
        balanceObject.current ??
        balanceObject.amount ??
        balanceObject.value ??
        balanceObject.before;
    }

    if (typeof candidate === "string") {
      candidate = candidate.replace(/,/g, "").trim();
    }

    const parsed = Number(candidate);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
  }

  onBalanceUpdate(callback: BalanceCallback): () => void {
    this.callbacks.add(callback);
    return () => this.callbacks.delete(callback);
  }

  requestBalance(): void {
    if (typeof window === "undefined" || window.parent === window) return;

    // Socket messages can contain the same result in multiple shapes. Avoid
    // sending duplicate parent requests for a single acknowledgement.
    const now = Date.now();
    if (now - this.lastBalanceRequestAt < 100) return;
    this.lastBalanceRequestAt = now;

    window.parent.postMessage({ type: "GET_BALANCE" } satisfies GameMessage, "*");
    window.parent.postMessage("getBalance", "*");
  }

  notifyGameReady(): void {
    this.sendToParent({ type: "GAME_READY" });
  }

  notifyBetPlaced(stake: number, betType: string): void {
    this.sendToParent({
      type: "BET_PLACED",
      payload: { stake, betType },
    });
  }

  notifyCashout(amount: number, multiplier: number): void {
    this.sendToParent({
      type: "CASHOUT",
      payload: { amount, multiplier },
    });
  }

  private sendToParent(message: GameMessage): void {
    if (typeof window === "undefined" || window.parent === window) return;

    try {
      window.parent.postMessage(message, "*");
    } catch {}
  }
}

export const ParentMessageService = new ParentBalanceServiceClass();
